import type { ChapterCharacterLayer, CharacterLayerNoteAnchor } from '../types/characterLayerTypes'
import type {
  LedgerSafeActiveLedgerRecord,
  LedgerSafeLedgerRecord,
  LedgerSafeLedgerStateRecord,
} from '../types/ledgerSafeLedgers'

type PrimarySnapshot = Partial<Record<string, any[]>>

const requireUniqueIds = (records: any[], field: string, label: string): Set<string> => {
  const ids = new Set<string>()
  records.forEach((record, index) => {
    const id = typeof record?.[field] === 'string' ? record[field] : ''
    if (!id) {
      throw new Error(`${label} record ${index + 1} has no ${field}`)
    }
    if (ids.has(id)) {
      throw new Error(`Duplicate ${label} ${field}: ${id}`)
    }
    ids.add(id)
  })
  return ids
}

const getNoteRowIds = (anchor: CharacterLayerNoteAnchor): string[] => {
  if (anchor.kind === 'span') return [anchor.locator.rowId]
  if (anchor.kind === 'witness-row' || anchor.kind === 'collation-row') return [anchor.rowId]
  if (anchor.kind === 'row-selection') return anchor.rowIds
  return []
}

const validateCharacterLayers = (
  snapshot: PrimarySnapshot,
  anchoredRowChapterById: Map<string, string>,
  witnessChapterById: Map<string, string>
): void => {
  const layers = (snapshot.chapter_character_layers || []) as ChapterCharacterLayer[]
  requireUniqueIds(layers, 'chapter_id', 'Character layer')
  for (const layer of layers) {
    const characterIds = requireUniqueIds(layer.characters || [], 'character_id', `Character in ${layer.chapter_id}`)
    requireUniqueIds(layer.occurrences || [], 'occurrence_id', `Character occurrence in ${layer.chapter_id}`)
    requireUniqueIds(layer.notes || [], 'note_id', `Character note in ${layer.chapter_id}`)

    const validateWitness = (witnessId: string | undefined, label: string): void => {
      if (!witnessId || witnessChapterById.get(witnessId) !== layer.chapter_id) {
        throw new Error(`${label} references missing or cross-context witness ${witnessId || '(empty)'}`)
      }
    }
    const validateRow = (rowId: string | undefined, label: string): void => {
      if (!rowId || anchoredRowChapterById.get(rowId) !== layer.chapter_id) {
        throw new Error(`${label} references missing or cross-context anchored row ${rowId || '(empty)'}`)
      }
    }

    for (const occurrence of layer.occurrences || []) {
      const referentIds = occurrence.referent_character_ids || [occurrence.character_id]
      for (const characterId of Array.from(new Set([occurrence.character_id, ...referentIds]))) {
        if (!characterIds.has(characterId)) {
          throw new Error(`Character occurrence ${occurrence.occurrence_id} references missing character ${characterId}`)
        }
      }
      validateRow(occurrence.locator?.rowId, `Character occurrence ${occurrence.occurrence_id}`)
      validateWitness(occurrence.locator?.witnessId, `Character occurrence ${occurrence.occurrence_id}`)
    }

    for (const note of layer.notes || []) {
      if (note.anchor?.kind === 'character' && !characterIds.has(note.anchor.characterId)) {
        throw new Error(`Character note ${note.note_id} references missing character ${note.anchor.characterId}`)
      }
      for (const rowId of note.anchor ? getNoteRowIds(note.anchor) : []) {
        validateRow(rowId, `Character note ${note.note_id}`)
      }
      if (note.anchor?.kind === 'span' || note.anchor?.kind === 'witness-row') {
        validateWitness(note.anchor.kind === 'span' ? note.anchor.locator.witnessId : note.anchor.witnessId, `Character note ${note.note_id}`)
      } else if (note.anchor?.kind === 'witness') {
        validateWitness(note.anchor.witnessId, `Character note ${note.note_id}`)
      } else if (note.anchor?.kind === 'row-selection' && note.anchor.witnessId) {
        validateWitness(note.anchor.witnessId, `Character note ${note.note_id}`)
      }
    }
  }
}

const validateLedgers = (
  snapshot: PrimarySnapshot,
  chapterIds: Set<string>,
  witnessChapterById: Map<string, string>
): void => {
  const ledgers = (snapshot.ledger_safe_ledgers || []) as LedgerSafeLedgerRecord[]
  const states = (snapshot.ledger_safe_ledger_states || []) as LedgerSafeLedgerStateRecord[]
  const activeLedgers = (snapshot.ledger_safe_active_ledgers || []) as LedgerSafeActiveLedgerRecord[]
  const ledgerIds = requireUniqueIds(ledgers, 'id', 'Ledger')
  const stateIds = requireUniqueIds(states, 'id', 'Ledger state')
  requireUniqueIds(activeLedgers, 'contextId', 'Active ledger')
  const ledgerById = new Map(ledgers.map(ledger => [ledger.id, ledger]))
  const stateById = new Map(states.map(state => [state.id, state]))

  for (const ledger of ledgers) {
    if (!chapterIds.has(ledger.contextId)) {
      throw new Error(`Ledger ${ledger.id} references missing context ${ledger.contextId}`)
    }
    if (!ledger.baselineStateId) {
      throw new Error(`Ledger ${ledger.id} has no baseline state pointer`)
    }
    for (const [kind, stateId] of [
      ['baseline', ledger.baselineStateId],
      ['latest working', ledger.latestWorkingStateId],
    ] as const) {
      if (!stateId) continue
      const state = stateById.get(stateId)
      if (!state || state.ledgerId !== ledger.id || state.contextId !== ledger.contextId) {
        throw new Error(`Ledger ${ledger.id} has an invalid ${kind} state pointer: ${stateId}`)
      }
      const expectedKind = kind === 'baseline' ? 'baseline' : 'working'
      if (state.kind !== expectedKind) {
        throw new Error(`Ledger ${ledger.id} ${kind} pointer targets a ${state.kind} state: ${stateId}`)
      }
    }
  }

  for (const state of states) {
    const ledger = ledgerById.get(state.ledgerId)
    if (!ledgerIds.has(state.ledgerId) || !ledger || ledger.contextId !== state.contextId) {
      throw new Error(`Ledger state ${state.id} references missing or incompatible ledger ${state.ledgerId}`)
    }
    if (state.parentStateId) {
      const parent = stateById.get(state.parentStateId)
      if (!parent || parent.ledgerId !== state.ledgerId) {
        throw new Error(`Ledger state ${state.id} has an invalid parent state ${state.parentStateId}`)
      }
    }
    if (!Array.isArray(state.rows) || state.rowCount !== state.rows.length) {
      throw new Error(`Ledger state ${state.id} row count does not match its rows`)
    }
    requireUniqueIds(state.rows, 'id', `Row in Ledger state ${state.id}`)
    for (const witnessState of state.witnessStates || []) {
      if (witnessChapterById.get(witnessState.witnessId) !== state.contextId) {
        throw new Error(`Ledger state ${state.id} references missing or cross-context witness ${witnessState.witnessId}`)
      }
    }
  }

  for (const active of activeLedgers) {
    const ledger = ledgerById.get(active.ledgerId)
    const state = stateById.get(active.stateId)
    if (!ledger || ledger.contextId !== active.contextId) {
      throw new Error(`Active ledger ${active.contextId} references missing or incompatible ledger ${active.ledgerId}`)
    }
    if (!state || state.ledgerId !== active.ledgerId || state.contextId !== active.contextId || state.kind !== active.stateKind) {
      throw new Error(`Active ledger ${active.contextId} references missing or incompatible state ${active.stateId}`)
    }
  }
}

const validateWitnessPayloadStores = (
  snapshot: PrimarySnapshot,
  witnessById: Map<string, any>
): void => {
  for (const [storeName, records] of [
    ['witness_text', snapshot.witness_text || []],
    ['witness_segmentation', snapshot.witness_segmentation || []],
  ] as const) {
    requireUniqueIds(records, 'witness_id', storeName)
    for (const record of records) {
      const witness = witnessById.get(record.witness_id)
      if (!witness) {
        throw new Error(`${storeName} record references missing witness ${record.witness_id}`)
      }
      if (record.schema_version !== 1) {
        throw new Error(`${storeName} record ${record.witness_id} has an unsupported schema version`)
      }
      if (
        !Number.isSafeInteger(record.source_witness_version)
        || record.source_witness_version < 0
        || (
          Number.isSafeInteger(witness.version)
          && record.source_witness_version > witness.version
        )
      ) {
        throw new Error(`${storeName} record ${record.witness_id} has an invalid source witness version`)
      }
    }
  }
}

export const validateAppStateSnapshotInvariants = (snapshot: PrimarySnapshot): void => {
  const anchoredRows = snapshot.anchored_rows || []
  requireUniqueIds(anchoredRows, 'id', 'Anchored row')
  const chapters = snapshot.chapters || []
  const witnesses = snapshot.witnesses || []
  const chapterIds = requireUniqueIds(chapters, 'id', 'Chapter')
  requireUniqueIds(witnesses, 'id', 'Witness')
  const anchoredRowChapterById = new Map(anchoredRows.map(row => [row.id, row.chapterId]))
  const witnessChapterById = new Map(witnesses.map(witness => [witness.id, witness.chapter_id]))
  const witnessById = new Map(witnesses.map(witness => [witness.id, witness]))
  validateWitnessPayloadStores(snapshot, witnessById)
  validateCharacterLayers(snapshot, anchoredRowChapterById, witnessChapterById)
  validateLedgers(snapshot, chapterIds, witnessChapterById)
}
